// @ts-nocheck
import React, { useEffect, useRef, useState } from 'react';

export function SeamlessLoopVideo({ src, poster, label, className = '' }) {
  const firstRef = useRef(null);
  const secondRef = useRef(null);
  const activeRef = useRef(0);
  const transitionRef = useRef(false);
  const timerRef = useRef(0);
  const idleRef = useRef(0);
  const visibleRef = useRef(false);
  const [front, setFront] = useState(0);
  const [enabled, setEnabled] = useState(false);
  const [standbyEnabled, setStandbyEnabled] = useState(false);

  useEffect(() => {
    const media = firstRef.current;
    const host = media?.closest('.scene-section') || media?.parentElement;
    if (!media || !host) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches || navigator.connection?.saveData === true) return;

    const setVisibility = visible => {
      visibleRef.current = visible;
      const videos = [firstRef.current, secondRef.current];
      if (visible) {
        setEnabled(true);
        videos[activeRef.current]?.play().catch(() => {});
      } else {
        videos.forEach(video => video?.pause());
      }
    };

    if (!('IntersectionObserver' in window)) { setVisibility(true); return; }
    const observer = new IntersectionObserver(
      entries => setVisibility(entries.some(entry => entry.isIntersecting)),
      { threshold: 0.01, rootMargin: '900px 0px' },
    );
    observer.observe(host);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!enabled) return;
    const first = firstRef.current;
    if (!first) return;

    const prepareStandby = () => {
      const schedule = window.requestIdleCallback
        ? callback => window.requestIdleCallback(callback, { timeout: 1200 })
        : callback => window.setTimeout(callback, 180);
      idleRef.current = schedule(() => setStandbyEnabled(true));
    };

    if (first.readyState >= HTMLMediaElement.HAVE_FUTURE_DATA) prepareStandby();
    else first.addEventListener('canplay', prepareStandby, { once: true });

    return () => {
      first.removeEventListener('canplay', prepareStandby);
      if (window.cancelIdleCallback) window.cancelIdleCallback(idleRef.current);
      else window.clearTimeout(idleRef.current);
    };
  }, [enabled, src]);

  useEffect(() => {
    if (!enabled) return;
    const videos = [firstRef.current, secondRef.current];
    const crossfade = () => {
      const outgoing = videos[activeRef.current];
      if (!visibleRef.current || !outgoing || transitionRef.current || !Number.isFinite(outgoing.duration) || outgoing.duration - outgoing.currentTime > 1.25) return;
      const next = activeRef.current === 0 ? 1 : 0;
      const incoming = videos[next];
      if (!incoming || incoming.readyState < HTMLMediaElement.HAVE_FUTURE_DATA) return;

      transitionRef.current = true;
      incoming.currentTime = 0;
      incoming.play().catch(() => {});
      setFront(next);
      timerRef.current = window.setTimeout(() => {
        outgoing.pause();
        outgoing.currentTime = 0;
        activeRef.current = next;
        transitionRef.current = false;
      }, 1200);
    };

    const replayIfNeeded = event => {
      if (transitionRef.current || !visibleRef.current) return;
      event.currentTarget.currentTime = 0;
      event.currentTarget.play().catch(() => {});
    };

    videos.forEach(video => video?.addEventListener('timeupdate', crossfade));
    videos.forEach(video => video?.addEventListener('ended', replayIfNeeded));
    if (visibleRef.current) videos[activeRef.current]?.play().catch(() => {});
    return () => {
      videos.forEach(video => video?.removeEventListener('timeupdate', crossfade));
      videos.forEach(video => video?.removeEventListener('ended', replayIfNeeded));
      window.clearTimeout(timerRef.current);
    };
  }, [src, enabled, standbyEnabled]);

  return (
    <>
      <video
        ref={firstRef}
        className={`loop-layer ${className} ${front === 0 ? 'is-front' : ''}`}
        muted
        playsInline
        preload={enabled ? 'auto' : 'none'}
        poster={poster}
        aria-label={label}
        src={enabled ? src : undefined}
      />
      <video
        ref={secondRef}
        className={`loop-layer ${className} ${front === 1 ? 'is-front' : ''}`}
        muted
        playsInline
        preload={standbyEnabled ? 'auto' : 'none'}
        poster={poster}
        aria-hidden="true"
        src={standbyEnabled ? src : undefined}
      />
    </>
  );
}
